import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function AutozoneNearMePage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '',
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : 'Near Me';

    const pageTitle = hasLocationFilter
        ? `AutoZone ${locationLabel} | Auto Parts, Batteries, Brakes & Accessories`
        : 'AutoZone Near Me | Auto Parts, Batteries, Brakes & Accessories';

    const pageDescription = hasLocationFilter
        ? `Find your nearest AutoZone store in ${locationLabel}. Quality auto parts, batteries, brakes, oil, tools and expert advice. Same-day pickup available.`
        : 'Find your nearest AutoZone store in South Africa. Quality auto parts, batteries, brakes, oil, tools and expert advice. Same-day pickup available.';

    const baseUrl = `${ENV.DOMAIN_URL}/autozone-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Midrand', 'Soweto'],
        Pretoria: ['Centurion', 'Akasia', 'Soshanguve', 'Mamelodi'],
        'Cape Town': ['Bellville', 'Somerset West', 'Paarl', 'Stellenbosch', 'Brackenfell'],
        Durban: ['Pinetown', 'Phoenix', 'Chatsworth', 'Umhlanga'],
    };

    const faqItems = [
        {
            question: 'Where can I find AutoZone stores near me in South Africa?',
            answer:
                'You can find AutoZone stores across South Africa using our store locator. Filter by province, city, or suburb to find your nearest AutoZone location. Major cities include Johannesburg, Cape Town, Durban, Pretoria, and Port Elizabeth.',
        },
        {
            question: 'What products and services does AutoZone offer?',
            answer:
                'AutoZone offers a comprehensive range of auto parts and accessories including batteries, brakes, oil and fluids, filters, wiper blades, lighting, tools, and diagnostic equipment. Many stores also provide free battery testing, wiper blade installation, and expert advice.',
        },
        {
            question: 'Does AutoZone offer same-day pickup?',
            answer:
                'Yes, AutoZone offers same-day pickup on most items when you order online. Simply select your nearest store at checkout, and pick up your parts the same day. Check individual store hours and availability.',
        },
        {
            question: 'Can I get my car battery tested at AutoZone for free?',
            answer:
                'Yes, most AutoZone stores offer free battery testing services. Bring your vehicle to your nearest AutoZone location, and their trained staff will test your battery, starter, and alternator at no charge.',
        },
        {
            question: "What are AutoZone's return and warranty policies?",
            answer:
                'AutoZone offers a satisfaction guarantee with easy returns on most products. Many parts come with limited lifetime warranties. Keep your receipt and packaging for returns. Contact your local AutoZone store for specific warranty details on your purchase.',
        },
    ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `AutoZone ${titleLocationLabel}`,
    description: pageDescription,
    url: canonicalUrl,
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="AutoZone near me, AutoZone South Africa, auto parts near me, car batteries, brake pads, oil change, car parts store, automotive supplies, car accessories, AutoZone stores, car repair parts" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Structured Data — merged into single @graph to avoid duplicate-field error */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              { ...structuredData, '@context': undefined },
              { ...faqStructuredData, '@context': undefined },
            ],
          }) }}
        />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-red-600 via-red-700 to-orange-600 py-16 text-white md:py-24">
            <div className="container relative z-10">
                <div className="mx-auto max-w-4xl text-center">
                    <h1 className="mb-4 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                        AutoZone {titleLocationLabel}
                    </h1>
                    <p className="mb-8 text-lg leading-relaxed text-red-50 md:text-xl">
                        Quality Auto Parts, Expert Advice, Same-Day Pickup
                    </p>
                    <div className="mb-6 flex flex-wrap justify-center gap-3 text-sm md:text-base">
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Free Battery Testing
                        </div>
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Expert Advice
                        </div>
                        <div className="flex items-center rounded-full border border-white/20 px-4 py-2">
                            <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9v-2h2v2zm0-4H9V5h2v4z" />
                            </svg>
                            Same-Day Pickup
                        </div>
                    </div>
                    <div className="mb-6 grid grid-cols-2 gap-3 text-center text-sm text-red-100 sm:grid-cols-4 md:grid-cols-6">
                        {quickCities.map((city) => (
                            <Link
                                key={city.name}
                                href={`/autozone-near-me/autozone/${city.province}/${slugify(city.name)}`}
                                className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                            >
                                {`AutoZone ${city.name}`}
                            </Link>
                        ))}
                    </div>
                    <div className="mt-5 grid gap-3 text-xs text-red-100 sm:grid-cols-3">
                        <div className="rounded-lg border border-white/10 px-3 py-2">Batteries & electrical</div>
                        <div className="rounded-lg border border-white/10 px-3 py-2">Brakes & suspension</div>
                        <div className="rounded-lg border border-white/10 px-3 py-2">Oil & fluids</div>
                    </div>
                </div>
            </div>
        </section>

        <div className="my-10">
            <div className="container" id="business-filters">
                <BusinessList
                    searchParams={searchParams}
                    defaultCategoryIds="5016"
                    initialBusinesses={businesses}
                    initialPagination={pagination}
                />
            </div>
        </div>

        <section className="container my-12">
            <div className="grid gap-8 lg:grid-cols-12">
                <div className="lg:col-span-8">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-2xl font-semibold text-slate-900">Your Trusted AutoZone Store</h2>
                        <p className="mt-3 text-sm text-slate-600">
                            Looking for AutoZone {locationSuffix}? CarSparesEzyFind helps you locate
                            AutoZone stores and auto parts centres across South Africa. AutoZone offers a comprehensive 
                            range of auto parts, batteries, brakes, oil, filters, tools, and diagnostic equipment at competitive prices. 
                            Use the filters to find your nearest AutoZone store by province, city, or suburb.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            Whether you need an AutoZone near me in Sandton, an AutoZone store in
                            Roodepoort, or AutoZone car parts in Randburg, this page helps you find the most
                            convenient location. AutoZone stores stock quality OEM and aftermarket parts, batteries with free 
                            testing, brake pads and rotors, engine oil and fluids, air and oil filters, wiper blades, lighting, 
                            and professional-grade tools. Many locations also offer free services including battery testing, 
                            wiper blade installation, and expert advice from knowledgeable staff.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            Popular searches include AutoZone Johannesburg Gauteng, AutoZone car parts Johannesburg,
                            AutoZone batteries Cape Town, and AutoZone brakes Durban.
                            Products include batteries and electrical components, brake pads and rotors, oil and fluids, 
                            air and oil filters, wiper blades, spark plugs, belts and hoses, lighting and bulbs, tools and 
                            equipment, and automotive accessories. Visit your nearest AutoZone for same-day pickup, expert 
                            advice, and quality parts backed by warranties.
                        </p>
                        <p className="mt-3 text-sm text-slate-600">
                            AutoZone stores are known for their knowledgeable staff who can help you find the right part 
                            for your vehicle. Most locations offer free battery testing, free wiper blade installation, and 
                            helpful advice for DIY enthusiasts and professional mechanics alike. Check with your local AutoZone 
                            store for specific product availability, services, operating hours, and current promotions. Many stores 
                            also offer online ordering with same-day pickup for added convenience.
                        </p>
                    </div>
                </div>
                <div className="lg:col-span-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h3 className="text-lg font-semibold text-slate-900">AutoZone Products & Services</h3>
                        <ul className="mt-4 space-y-2 text-sm text-slate-600">
                            <li>Batteries & electrical</li>
                            <li>Brakes & suspension</li>
                            <li>Oil & fluids</li>
                            <li>Filters & maintenance</li>
                            <li>Tools & equipment</li>
                            <li>Wiper blades & lighting</li>
                            <li>Spark plugs & ignition</li>
                            <li>Belts, hoses & cooling</li>
                            <li>Steering & chassis</li>
                            <li>Engine management</li>
                            <li>Automotive accessories</li>
                            <li>Free battery testing</li>
                            <li>Expert advice</li>
                            <li>Same-day pickup</li>
                        </ul>
                    </div>
                </div>
              </div>
        </section>

        <section className="container my-12">
            <div className="grid gap-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">AutoZone by Province</h2>
                        <div className="mt-4 grid gap-2 sm:grid-cols-2">
                            {quickProvinces.map((province) => (
                                <Link
                                    key={province}
                                    href={`/autozone-near-me/autozone/${slugify(province)}`}
                                    className="rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700 hover:border-red-600 hover:text-red-600"
                                >
                                    {`AutoZone ${province}`}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-7">
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900">
                            Frequently Asked Questions (AutoZone SEO)
                        </h2>
                        <div className="mt-4 space-y-4">
                            {faqItems.map((item) => (
                                <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                    <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <DownloadApp />
      </div>
    </>
  );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '5016',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
